import React, { useContext, useEffect, useState } from 'react';
import SelectInput, {
  SelectInputHelper
} from '../../common/SelectInput/SelectInput';
import './NeedHelpPage.scss';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { useNavigate, useOutletContext } from 'react-router-dom';
import {
  DefaultRadioOptions,
  InsuranceJourneyHelperText
} from '../../../consts/Common';
import { SiteContext } from '../../../context/SiteContext';
import Links from '../../common/Widgets/Links';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import TextInput from '../../common/TextInput/TextInput';
import {
  CarInsuranceVehicleModel,
  DropdownData,
  InsuranceJourneyContext,
  KeyValuePair,
  SelectOpt,
  VehicleModificationError,
  VehicleType
} from '../../common/CommonTypes';
import { logError, populateSelectOptions } from '../../common/HelperMethods';
import ClickableHelpText from '../../common/HelpText/ClickableHelpText';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import RadioOptions from '../../common/RadioOptions/RadioOptions';
import DateInput from '../../common/DateInput/DateInput';
import useCarInsuranceController from '../../../hooks/insuranceJourney/useCarInsuranceController';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { PagesWithDropdown } from '../../common/Constants';

type LotusUpdateDetailsFields = {
  confirmInformation: boolean;
  expectedDeliveryDate: boolean;
  coverStartDate: Date | undefined;
  annualCoverStartDate: Date | undefined;
  coverStartTime: string;
  vrnKnown: boolean;
  userVrn: string;
  manufacturer: string;
  model: string;
  fuelType: string;
  transmission: string;
  numberOfDoors: string;
  isItNewOrUsed: string;
  vehicleSpecification: string;
  yearOfManufacture: string;
  typeOfTrackingDevice: string;
  modifications: VehicleModificationError[];
  keysPickupDate: string;
  isFittedWithAntiTheftDevice: string;
  isVehicleModified: string;
  transmissionType: string;
  vehicle: VehicleType | undefined;
};

type LotusUpdateDetailsErrors = {
  confirmInformation: boolean;
  expectedDeliveryDate: boolean;
  coverStartDate: string;
  annualCoverStartDate: string;
  coverStartTime: string;
  vrnKnown: string;
  userVrn: string;
  manufacturer: string;
  model: string;
  fuelType: string;
  transmission: string;
  numberOfDoors: string;
  isItNewOrUsed: string;
  vehicleSpecification: string;
  yearOfManufacture: string;
  typeOfTrackingDevice: string;
  modifications: VehicleModificationError[];
  keysPickupDate: string;
  isFittedWithAntiTheftDevice: string;
  isVehicleModified: string;
  transmissionType: string;
  vehicle: VehicleType | undefined;
};

const initialFormValues: LotusUpdateDetailsFields = {
  confirmInformation: true,
  expectedDeliveryDate: false,
  coverStartDate: undefined,
  annualCoverStartDate: undefined,
  coverStartTime: '',
  vrnKnown: false,
  userVrn: '',
  manufacturer: '',
  model: '',
  fuelType: '',
  transmission: '',
  numberOfDoors: '',
  isItNewOrUsed: '',
  vehicleSpecification: '',
  yearOfManufacture: '',
  typeOfTrackingDevice: '',
  modifications: [],
  keysPickupDate: '',
  isFittedWithAntiTheftDevice: '',
  isVehicleModified: '',
  transmissionType: '',
  vehicle: undefined
};

const initialFormErrors: LotusUpdateDetailsErrors = {
  confirmInformation: false,
  expectedDeliveryDate: false,
  coverStartDate: '',
  annualCoverStartDate: '',
  coverStartTime: '',
  vrnKnown: '',
  userVrn: '',
  manufacturer: '',
  model: '',
  fuelType: '',
  transmission: '',
  numberOfDoors: '',
  isItNewOrUsed: '',
  vehicleSpecification: '',
  yearOfManufacture: '',
  typeOfTrackingDevice: '',
  modifications: [],
  keysPickupDate: '',
  isFittedWithAntiTheftDevice: '',
  isVehicleModified: '',
  transmissionType: '',
  vehicle: undefined
};

const LotusUpdateDetailsPage = (): JSX.Element => {
  const navigate = useNavigate();
  const [showNonSchemePopup, setShowNonSchemePopup] = useState(false);

  const [formValues, setFormValues] =
    useState<LotusUpdateDetailsFields>(initialFormValues);

  const [formErrors, setFormErrors] =
    useState<LotusUpdateDetailsErrors>(initialFormErrors);

  const [showVehicleSection, setShowVehicleSection] = useState(false);

  const [showCoverDetailSection, setShowCoverDetailSection] = useState(false);

  const { site } = useContext(SiteContext);

  const token = site.user.token;

  const {
    confirmInformation,
    expectedDeliveryDate,
    coverStartDate,
    annualCoverStartDate,
    coverStartTime
  } = formValues;

  const today = new Date();

  const [coverStartTimeOptions, setCoverStartTimeOptions] = useState<
    SelectOpt[]
  >([]);

  const { journey, pageEvents } = useOutletContext<InsuranceJourneyContext>();
  const { vehicleInfo, staticData } = journey;
  const { vehicleDetailsPage, commonEvents } = pageEvents;
  const { setStaticData } = commonEvents;

  const { onChangeManufacturer, onChangeModel, onChangeVehicleSpecification } =
    vehicleDetailsPage;

  const {
    getVehicleDetailsPageDropdownValues,
    getDropDownValuesForVehicleSpecifications,
    getDropdownValuesForVehicleMake,
    getDropdownValuesForVehicleModel
  } = useCarInsuranceController();

  const {
    vrnKnown,
    userVrn,
    fuelType,
    manufacturer,
    model,
    numberOfDoors,
    transmissionType,
    vehicleSpecification
  } = vehicleInfo;
  const { onChangeVrnKnown } = vehicleDetailsPage;

  const manufacturersOptions =
    staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
        d.controlName === 'vehicle-makes'
    )?.values ?? [];

  const modelsOptions =
    staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
        d.controlName === 'models'
    )?.values ?? [];

  const vehicleSpecificationsOptions =
    staticData?.find(
      (d) =>
        d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
        d.controlName === 'vehicle-specifications'
    )?.values ?? [];

  let initialStaticData = staticData ? [...staticData] : [];

  useEffect(() => {
    window.scrollTo(0, 0);
    LoadVehicleDetails();
    getCoverStartTimeOptions();
    onChangingManufacturer(site.brand.brandName.toUpperCase());
  }, []);

  useEffect(() => {
    loadVehicleSpecification();
  }, [model]);

  //vehicle

  const getDropDownValuesForVehicleSpecificationsFailure = (err: string) => {
    logError(err);
  };

  const getSpecificationsSuccess = (vehicleSpecifications: KeyValuePair[]) => {
    const newStaticDataItem = {
      page: PagesWithDropdown.InsuranceVehicleDetailsPage,
      controlName: 'vehicle-specifications',
      values: populateSelectOptions(vehicleSpecifications)
    };

    setStaticData([...initialStaticData, newStaticDataItem]);
  };
  const loadVehicleSpecification = () => {
    if (model && model !== SelectInputHelper.NullValue()) {
      getDropDownValuesForVehicleSpecifications(
        manufacturer,
        model,
        fuelType,
        transmissionType,
        numberOfDoors,
        token,
        getSpecificationsSuccess,
        getDropDownValuesForVehicleSpecificationsFailure
      );
    } else {
      setStaticData(initialStaticData);
    }
  };

  useEffect(() => {
    if (manufacturer && manufacturer !== SelectInputHelper.NullValue()) {
      showNoneSchemePopup(manufacturer);
    }
  }, [manufacturer]);

  const showNoneSchemePopup = (manufacturer: string) => {
    const make = getManufacturerName(manufacturer);
    if (
      make &&
      make !== SelectInputHelper.NullValue() &&
      make.toLowerCase() !== site.brand.brandName.toLowerCase() &&
      site.brand.brandName.toLowerCase() !== 'original'
    ) {
      setShowNonSchemePopup(true);
    }
  };

  const getManufacturerName = (value: string) => {
    return manufacturersOptions.find((m) => m.value === value)?.text ?? '';
  };
  const removeDropDownData = (
    data: DropdownData[],
    dataToRemove: string
  ): DropdownData[] => {
    return data.filter(
      (d) =>
        !(
          d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
          d.controlName === dataToRemove
        )
    );
  };

  const getDropdownValuesForVehicleModelsSuccess = (
    vehicleModels: KeyValuePair[]
  ) => {
    const newStaticDataItem = {
      page: PagesWithDropdown.InsuranceVehicleDetailsPage,
      controlName: 'models',
      values: populateSelectOptions(vehicleModels)
    };

    if (!staticData) {
      setStaticData([newStaticDataItem]);
    } else {
      const hasModelsOptions = staticData.find(
        (d) =>
          d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
          d.controlName === 'models'
      );

      const updatedStaticData: DropdownData[] = removeDropDownData(
        staticData,
        'vehicle-specifications'
      );

      if (hasModelsOptions) {
        const newStaticData = updatedStaticData.map((d) => {
          if (
            d.page === PagesWithDropdown.InsuranceVehicleDetailsPage &&
            d.controlName === 'models'
          ) {
            return newStaticDataItem;
          } else {
            return d;
          }
        });

        setStaticData(newStaticData);
      } else {
        setStaticData([...updatedStaticData, newStaticDataItem]);
      }
    }
  };

  const onChangingManufacturer = (value: string) => {
    onChangeManufacturer(value);

    if (value && value !== SelectInputHelper.NullValue()) {
      getDropdownValuesForVehicleModel(
        value,
        token,
        getDropdownValuesForVehicleModelsSuccess,
        logError
      );
    } else {
      setStaticData(removeDropDownData(staticData, 'models'));
    }
  };

  const onChangingModel = (value: string) => {
    onChangeModel(value);
    setStaticData(removeDropDownData(staticData, 'vehicle-specifications'));
  };

  //end vehicle
  const onDateChange = (e: React.FormEvent<HTMLInputElement>, key: string) => {
    const value = (e.target as HTMLInputElement).value;
    const updatedFormValues = { ...formValues, [key]: value };

    setFormValues(updatedFormValues);
  };

  const LoadVehicleDetails = () => {
    const getModelsSuccess = (vehicleModels: KeyValuePair[]) => {
      const newStaticDataItem = {
        page: PagesWithDropdown.InsuranceVehicleDetailsPage,
        controlName: 'models',
        values: populateSelectOptions(vehicleModels)
      };

      initialStaticData = [...initialStaticData, newStaticDataItem];
    };

    const getMakesSuccess = (vehicleMakes: KeyValuePair[]) => {
      const newStaticDataItem = {
        page: PagesWithDropdown.InsuranceVehicleDetailsPage,
        controlName: 'vehicle-makes',
        values: populateSelectOptions(vehicleMakes)
      };

      initialStaticData = [...initialStaticData, newStaticDataItem];

      if (manufacturer && manufacturer !== SelectInputHelper.NullValue()) {
        getDropdownValuesForVehicleModel(
          manufacturer,
          token,
          getModelsSuccess,
          logError
        );
      } else {
        setStaticData(initialStaticData);
      }
    };
    const getVehicleDetailsPageDropdownValuesFailure = (err: string) => {
      console.log(`Get title error: ${err}`);
    };
    const getPageDataSuccess = (dropdownValues: CarInsuranceVehicleModel) => {
      const newValues = [
        {
          page: PagesWithDropdown.InsuranceVehicleDetailsPage,
          controlName: 'dealers',
          values: populateSelectOptions(dropdownValues.dealers)
        },
        {
          page: PagesWithDropdown.InsuranceVehicleDetailsPage,
          controlName: 'fuel-types',
          values: populateSelectOptions(dropdownValues.fuelTypes)
        },
        {
          page: PagesWithDropdown.InsuranceVehicleDetailsPage,
          controlName: 'modification-types',
          values: populateSelectOptions(dropdownValues.modificationTypes)
        },
        {
          page: PagesWithDropdown.InsuranceVehicleDetailsPage,
          controlName: 'no-of-doors',
          values: populateSelectOptions(dropdownValues.numberOfDoors)
        },
        {
          page: PagesWithDropdown.InsuranceVehicleDetailsPage,
          controlName: 'vendors',
          values: populateSelectOptions(dropdownValues.vendors)
        },
        {
          page: PagesWithDropdown.InsuranceVehicleDetailsPage,
          controlName: 'year-of-manufacture',
          values: populateSelectOptions(dropdownValues.yearOfManufacture)
        },
        {
          page: PagesWithDropdown.InsuranceVehicleDetailsPage,
          controlName: 'new-used',
          values: populateSelectOptions(dropdownValues.newOrUsed)
        }
      ];

      initialStaticData = [...initialStaticData, ...newValues];

      getDropdownValuesForVehicleMake(token, getMakesSuccess, logError);
    };

    const pageData = staticData?.find(
      (d) => d.page === PagesWithDropdown.InsuranceVehicleDetailsPage
    );

    if (!pageData) {
      getVehicleDetailsPageDropdownValues(
        site.brand.tiaProductCode,
        token,
        getPageDataSuccess,
        getVehicleDetailsPageDropdownValuesFailure
      );
    }
  };

  const getCoverStartTimeOptions = (startFrom?: number) => {
    const options = [];

    for (let i = startFrom ?? 0; i <= 23; i++) {
      const timeValue = i < 10 ? '0' + i + ':00' : i + ':00';
      const item: SelectOpt = {
        id: 'T' + i,
        value: timeValue,
        text: timeValue,
        disabled: false
      };
      options.push(item);
    }

    setCoverStartTimeOptions(options);
  };

  const goBack = (): void => {
    navigate(-1);
  };

  const validatePersonalDetails = (): boolean => {
    let isValid = true;

    const error: LotusUpdateDetailsErrors = {
      ...initialFormErrors
    };

    if (CommonValidation.isEmpty(coverStartDate)) {
      isValid = false;
      error.coverStartDate = 'Start date is required';
    }

    if (!SelectInputHelper.isValidValue(coverStartTime)) {
      isValid = false;
      error.coverStartTime = 'Cover start time is required';
    }

    setFormErrors(error);
    return isValid;
  };

  const onChangingConfirmInformation = (value: boolean) => {
    setShowVehicleSection(!value);
    const updatedFormValues = { ...formValues, confirmInformation: value };
    setFormValues(updatedFormValues);
  };

  const onChangingExpectedDeliveryDate = (value: boolean) => {
    setShowCoverDetailSection(value);
    const updatedFormValues = { ...formValues, expectedDeliveryDate: value };
    setFormValues(updatedFormValues);
  };

  const onChangingVrnKnown = (vrnKnown: boolean) => {
    const brand = site.brand.brandName.toUpperCase();
    onChangeVrnKnown(vrnKnown, brand);
  };

  const onTextChange = (e: React.FormEvent<HTMLInputElement>, key: string) => {
    const updatedFormValues = { ...formValues, [key]: e.currentTarget.value };

    setFormValues(updatedFormValues);
  };

  const onChangeCoverStartTime = (value: string) => {
    const updatedFormValues = { ...formValues, coverStartTime: value };

    setFormValues(updatedFormValues);
  };

  const onContinueClick = () => {
    const isValid = validatePersonalDetails();
    if (isValid) {
      console.log('save quote');
    } else {
      console.log('Not Valid Page');
    }
  };

  //end vehicle
  const chevronLeftComponent = <FontAwesomeIcon icon={faChevronLeft} />;

  return (
    <div>
      <Links
        id='back-link'
        title=''
        items={[{ to: '', text: 'Back', onClick: goBack }]}
        icon={chevronLeftComponent}
      />
      <p>
        {' '}
        Please review the vehicle information currently saved on your quote
      </p>
      <h4 className='section-header m1-top'>Update details</h4>
      <div className='table-flex-layout'>
        <div className='form-container'>
          <div className='one-col'>
            <RadioOptions
              errorMessage=''
              selectedOption={
                confirmInformation === null
                  ? ''
                  : confirmInformation
                    ? 'Yes'
                    : 'No'
              }
              optionsList={DefaultRadioOptions}
              setSelectedOption={(value) =>
                onChangingConfirmInformation(value === 'Yes')
              }
              label={'Please confirm if this is correct?'}
              name='confirm-information'
            />
          </div>
          {showVehicleSection && (
            <>
              <div className='two-col'>
                <div className='control-label'>Manufacturer</div>
                <div className='control'>
                  <SelectInput
                    name='manufacturer'
                    className='input-text w250'
                    value={manufacturer}
                    options={manufacturersOptions}
                    onChange={onChangingManufacturer}
                    addDefaultOption
                    error={formErrors.manufacturer}
                  />
                </div>
              </div>
              <div className='two-col'>
                <div className='control-label'>Model</div>
                <div className='control'>
                  <SelectInput
                    name='Model'
                    className='input-text w250'
                    value={model}
                    options={modelsOptions}
                    onChange={onChangingModel}
                    addDefaultOption
                    allowZeroOptions
                    error={formErrors.model}
                  />
                </div>
              </div>

              <div className='one-col'>
                <div className='control-label help-text'>
                  Please select the vehicle specification from the list
                  <ClickableHelpText
                    helpText={InsuranceJourneyHelperText.Pages.VehicleDetails.VehicleSpecification.replace(
                      '[Phone]',
                      site.brand.brandPhoneNumbers.quotesAndRenewalsNumber
                    )}
                  />
                </div>
                <div className='control'>
                  <SelectInput
                    name='vehicle-specifications'
                    className='input-text w250'
                    value={vehicleSpecification}
                    options={vehicleSpecificationsOptions}
                    onChange={onChangeVehicleSpecification}
                    addDefaultOption
                    error={formErrors.vehicleSpecification}
                  />
                </div>
              </div>
            </>
          )}

          <div className='one-col'>
            <RadioOptions
              errorMessage={formErrors.vrnKnown}
              selectedOption={vrnKnown === null ? '' : vrnKnown ? 'Yes' : 'No'}
              optionsList={DefaultRadioOptions}
              setSelectedOption={(value) => onChangingVrnKnown(value === 'Yes')}
              label={'Do you know your vehicle registration number?'}
              name='vrn-known'
            />
            {vrnKnown && (
              <div className='one-col'>
                <div className='control-label'>Registration number</div>
                <div className='flex-row'>
                  <div className='control flex-column'>
                    <TextInput
                      placeholder='Registration number'
                      name='registration-number'
                      className='input-text w250'
                      value={userVrn}
                      onChange={(e: React.FormEvent<HTMLInputElement>) =>
                        onTextChange(e, 'userVrn')
                      }
                      error={formErrors.userVrn}
                      readOnly={false}
                    />
                  </div>
                </div>
              </div>
            )}

            <div className='one-col'>
              <RadioOptions
                errorMessage=''
                selectedOption={
                  expectedDeliveryDate === null
                    ? ''
                    : expectedDeliveryDate
                      ? 'Yes'
                      : 'No'
                }
                optionsList={DefaultRadioOptions}
                setSelectedOption={(value) =>
                  onChangingExpectedDeliveryDate(value === 'Yes')
                }
                label={'Has your expected delivery date changed?'}
                name='expected-deliveryDate-confirmation'
              />
            </div>
            {showCoverDetailSection && (
              <>
                <div className='one-col'>
                  <div className='control-label'>
                    When do you hope to pick up the keys?
                  </div>
                  <div className='control'>
                    <DateInput
                      id='coverStart-date-id'
                      name='coverStart-date-date'
                      value={coverStartDate}
                      useJson
                      forceTimeReset
                      minimumDate={today}
                      onDateChanged={(e: React.FormEvent<HTMLInputElement>) =>
                        onDateChange(e, 'coverStartDate')
                      }
                      culture={site.cultureCode}
                      error={formErrors.coverStartDate}
                    />
                  </div>
                </div>
                <div className='one-col'>
                  <div className='control-label'>
                    When would you like your annual cover to start within the
                    next 30 days?
                  </div>
                  <div className='control'>
                    <DateInput
                      id='annualcoverStart-date-id'
                      name='annualcoverStart-date-date'
                      value={annualCoverStartDate}
                      useJson
                      forceTimeReset
                      minimumDate={today}
                      onDateChanged={(e: React.FormEvent<HTMLInputElement>) =>
                        onDateChange(e, 'annualCoverStartDate')
                      }
                      culture={site.cultureCode}
                      error={formErrors.coverStartDate}
                    />
                  </div>
                </div>
                <div className='one-col'>
                  <div className='control-label help-text'>
                    Cover start time ( Hour - Minute )
                    <ClickableHelpText
                      helpText={
                        InsuranceJourneyHelperText.Pages.CoverDetails
                          .CoverStartTime
                      }
                    />
                  </div>
                  <div className='control'>
                    <SelectInput
                      name='coverStartTime'
                      value={coverStartTime}
                      options={coverStartTimeOptions}
                      onChange={onChangeCoverStartTime}
                      forceDefault
                      addDefaultOption
                      error={formErrors.coverStartTime}
                    />
                  </div>
                </div>
              </>
            )}
            <div>
              <ButtonContinue
                name='btn-save-quote'
                text='Submit'
                onClick={onContinueClick}
                additionalClassNames='btn-save'
              />
            </div>

            <div className='flex-row'>
              <ButtonBack
                name='btnBack'
                text='Back'
                onClick={goBack}
                additionalClassNames='btn-back'
              />
            </div>
          </div>
        </div>
      </div>
      <Modal
        role='non-scheme-modal-popup'
        isOpen={showNonSchemePopup}
        className={`insurance-${site.brand.brandName}`}
        toggle={() => setShowNonSchemePopup(false)}>
        <ModalHeader toggle={() => setShowNonSchemePopup(false)}>
          Please Note
        </ModalHeader>
        <ModalBody>
          The vehicle to be insured is NON-SCHEME. Please advise the customer to
          contact Original Insurance on 0800 035 2715
        </ModalBody>
        <ModalFooter>
          <ButtonContinue
            name='modal-btn-continue'
            text='Continue'
            onClick={() => setShowNonSchemePopup(false)}
            additionalClassNames='btn-ok'
          />
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default LotusUpdateDetailsPage;
